import { isNonProduction } from "@/utils/common";
import html2canvas from "html2canvas-pro";
import { createRoot } from "react-dom/client";
import { sleep } from "radash";
import {
  WebStoryEndCard,
  type WebStoryBrandingDetails,
} from "../components/web-story-end-card";

function loadCanvasAsImage(
  canvas: HTMLCanvasElement
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Failed to convert end card canvas to image"));
    image.src = canvas.toDataURL("image/png");
  });
}

export async function captureWebStoryEndCardImage(
  details: WebStoryBrandingDetails,
  width: number,
  height: number
): Promise<HTMLImageElement> {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-9999px";
  host.style.top = "0";
  host.style.pointerEvents = "none";
  document.body.appendChild(host);

  const root = createRoot(host);

  try {
    root.render(
      <WebStoryEndCard details={details} width={width} height={height} />
    );

    await sleep(800);

    const element = host.firstElementChild as HTMLElement | null;
    if (!element) {
      throw new Error("Failed to render end card");
    }

    const canvas = await html2canvas(element, {
      width,
      height,
      scale: 1,
      useCORS: true,
      allowTaint: true,
      logging: isNonProduction(),
      onclone: async () => {
        await sleep(500);
      },
    });

    return loadCanvasAsImage(canvas);
  } finally {
    root.unmount();
    document.body.removeChild(host);
  }
}
